import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import RoleProtectedRoute from '@/components/RoleProtectedRoute';
import { Link } from 'react-router-dom';
import { 
  Calendar,
  CreditCard,
  Users,
  Search,
  DollarSign,
  UserPlus,
  XCircle,
  Clock,
  TrendingUp,
  Settings
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const StaffDashboard = () => {
  const { user } = useAuth();
  
  // Mock data for demonstration
  const todayStats = {
    revenue: 2850,
    appointments: 18,
    newMembers: 5,
    noShows: 2
  };

  const quickActions = [
    { title: 'Book Treatment', icon: Calendar, href: '/staff/appointments', color: '#A55C52' },
    { title: 'Patient Lookup', icon: Search, href: '/staff/patients', color: '#CDA79D' },
    { title: 'Process Payment', icon: CreditCard, href: '/staff/payments', color: '#5C322B' },
    { title: 'Log Treatment', icon: Clock, href: '/staff/treatments', color: 'linear-gradient(135deg, #A55C52, #5C322B)' }
  ];

  const upcomingAppointments = [
    { id: 1, patient: 'Sarah Johnson', time: '10:00 AM', treatment: 'Botox', provider: 'Dr. Smith', status: 'confirmed' },
    { id: 2, patient: 'Mike Chen', time: '11:30 AM', treatment: 'HydraFacial', provider: 'Emily R.', status: 'pending' },
    { id: 3, patient: 'Lisa Wong', time: '2:00 PM', treatment: 'Lip Filler', provider: 'Dr. Smith', status: 'confirmed' }
  ];

  return (
    <RoleProtectedRoute allowedRoles={['admin', 'coordinator']}>
      <div className="min-h-screen" style={{ background: 'linear-gradient(to bottom right, #F2D9D3, #FAF8F6)' }}>
        {/* Header */}
        <div className="bg-white shadow-sm border-b" style={{ borderColor: 'rgba(205, 167, 157, 0.3)' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-6">
              <div>
                <h1 className="text-3xl font-bold font-juv-heading" style={{ color: '#5C322B' }}>
                  Staff Dashboard
                </h1>
                <p className="mt-1" style={{ color: 'rgba(92, 50, 43, 0.7)' }}>
                  Welcome back, {user?.email?.split('@')[0]}
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <Link 
                  to="/staff/dashboard/enhanced"
                  className="text-sm px-4 py-2 bg-juv-terracotta text-white rounded-lg hover:opacity-90 transition-colors"
                >
                  ✨ Try Enhanced Dashboard
                </Link>
                <Link 
                  to="/dashboard"
                  className="text-sm hover:opacity-70 transition-colors"
                  style={{ color: '#A55C52' }}
                >
                  ← Back to Portal
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="space-y-8">
            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="bg-white shadow-luxury" style={{ borderColor: 'rgba(205, 167, 157, 0.3)' }}>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4" style={{ backgroundColor: 'rgba(165, 92, 82, 0.2)' }}>
                      <DollarSign className="h-6 w-6" style={{ color: '#A55C52' }} />
                    </div>
                    <div>
                      <p className="text-sm" style={{ color: 'rgba(92, 50, 43, 0.7)' }}>Today's Revenue</p>
                      <p className="text-2xl font-bold font-juv-heading" style={{ color: '#5C322B' }}>
                        ${todayStats.revenue.toLocaleString()}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white shadow-luxury" style={{ borderColor: 'rgba(205, 167, 157, 0.3)' }}>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4" style={{ backgroundColor: 'rgba(205, 167, 157, 0.2)' }}>
                      <Calendar className="h-6 w-6" style={{ color: '#CDA79D' }} />
                    </div>
                    <div>
                      <p className="text-sm" style={{ color: 'rgba(92, 50, 43, 0.7)' }}>Today's Appointments</p>
                      <p className="text-2xl font-bold font-juv-heading" style={{ color: '#5C322B' }}>
                        {todayStats.appointments}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white shadow-luxury" style={{ borderColor: 'rgba(205, 167, 157, 0.3)' }}>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4" style={{ backgroundColor: 'rgba(92, 50, 43, 0.2)' }}>
                      <UserPlus className="h-6 w-6" style={{ color: '#5C322B' }} />
                    </div>
                    <div>
                      <p className="text-sm" style={{ color: 'rgba(92, 50, 43, 0.7)' }}>New Members This Week</p>
                      <p className="text-2xl font-bold font-juv-heading" style={{ color: '#5C322B' }}>
                        {todayStats.newMembers}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white shadow-luxury" style={{ borderColor: 'rgba(205, 167, 157, 0.3)' }}>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4" style={{ backgroundColor: 'rgba(220, 38, 127, 0.1)' }}>
                      <XCircle className="h-6 w-6 text-red-500" />
                    </div>
                    <div>
                      <p className="text-sm" style={{ color: 'rgba(92, 50, 43, 0.7)' }}>No-Shows This Week</p>
                      <p className="text-2xl font-bold font-juv-heading" style={{ color: '#5C322B' }}>
                        {todayStats.noShows}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Quick Actions */}
              <Card className="bg-white shadow-luxury" style={{ borderColor: 'rgba(205, 167, 157, 0.3)' }}>
                <CardHeader>
                  <CardTitle className="font-juv-heading" style={{ color: '#5C322B' }}>Quick Actions</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    {quickActions.map((action, index) => {
                      const Icon = action.icon;
                      return (
                        <Button
                          key={index}
                          asChild
                          className="h-20 flex-col space-y-2 text-white hover:scale-105 transition-all duration-300"
                          style={{ background: action.color }}
                        >
                          <Link to={action.href}>
                            <Icon className="h-5 w-5" />
                            <span className="text-sm font-medium">{action.title}</span>
                          </Link>
                        </Button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Upcoming Appointments */}
              <Card className="bg-white shadow-luxury" style={{ borderColor: 'rgba(205, 167, 157, 0.3)' }}>
                <CardHeader>
                  <CardTitle className="font-juv-heading flex items-center" style={{ color: '#5C322B' }}>
                    <Clock className="h-5 w-5 mr-2" style={{ color: '#A55C52' }} />
                    Next Appointments
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {upcomingAppointments.map((appointment) => (
                      <div key={appointment.id} className="flex items-center justify-between p-4 rounded-lg" style={{ backgroundColor: 'rgba(242, 217, 211, 0.3)' }}>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium" style={{ color: '#5C322B' }}>{appointment.patient}</h4>
                            <Badge 
                              variant={appointment.status === 'confirmed' ? 'default' : 'secondary'}
                              className={appointment.status === 'confirmed' ? 'text-white' : ''}
                              style={{ 
                                backgroundColor: appointment.status === 'confirmed' ? '#A55C52' : '#CDA79D',
                                color: appointment.status === 'confirmed' ? 'white' : '#5C322B'
                              }}
                            >
                              {appointment.status}
                            </Badge>
                          </div>
                          <p className="text-sm" style={{ color: 'rgba(92, 50, 43, 0.7)' }}>
                            {appointment.time} • {appointment.treatment} • {appointment.provider}
                          </p>
                        </div>
                      </div>
                    ))}
                    <Button 
                      asChild
                      variant="outline" 
                      className="w-full hover:bg-white/80" 
                      style={{ borderColor: 'rgba(205, 167, 157, 0.5)', color: '#5C322B' }}
                    >
                      <Link to="/staff/appointments">View All Appointments</Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Navigation Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card className="bg-white shadow-luxury hover:shadow-xl transition-shadow cursor-pointer" style={{ borderColor: 'rgba(205, 167, 157, 0.3)' }}>
                <Link to="/staff/patients">
                  <CardContent className="p-6 text-center">
                    <Users className="h-12 w-12 mx-auto mb-4" style={{ color: '#A55C52' }} />
                    <h3 className="text-lg font-juv-heading" style={{ color: '#5C322B' }}>Patient Management</h3>
                    <p className="text-sm mt-2" style={{ color: 'rgba(92, 50, 43, 0.7)' }}>
                      Search, view profiles, and manage patient records
                    </p>
                  </CardContent>
                </Link>
              </Card>

              <Card className="bg-white shadow-luxury hover:shadow-xl transition-shadow cursor-pointer" style={{ borderColor: 'rgba(205, 167, 157, 0.3)' }}>
                <Link to="/staff/credits">
                  <CardContent className="p-6 text-center">
                    <CreditCard className="h-12 w-12 mx-auto mb-4" style={{ color: '#CDA79D' }} />
                    <h3 className="text-lg font-juv-heading" style={{ color: '#5C322B' }}>Credit Management</h3>
                    <p className="text-sm mt-2" style={{ color: 'rgba(92, 50, 43, 0.7)' }}>
                      Add, adjust, and track JUV credits for patients
                    </p>
                  </CardContent>
                </Link>
              </Card>

              <Card className="bg-white shadow-luxury hover:shadow-xl transition-shadow cursor-pointer" style={{ borderColor: 'rgba(205, 167, 157, 0.3)' }}>
                <Link to="/staff/payments">
                  <CardContent className="p-6 text-center">
                    <TrendingUp className="h-12 w-12 mx-auto mb-4" style={{ color: '#A55C52' }} />
                    <h3 className="text-lg font-juv-heading" style={{ color: '#5C322B' }}>Payments</h3>
                    <p className="text-sm mt-2" style={{ color: 'rgba(92, 50, 43, 0.7)' }}>
                      Process payments and manage transactions
                    </p>
                  </CardContent>
                </Link>
              </Card>

              <Card className="bg-white shadow-luxury hover:shadow-xl transition-shadow cursor-pointer" style={{ borderColor: 'rgba(205, 167, 157, 0.3)' }}>
                <Link to="/staff/reports">
                  <CardContent className="p-6 text-center">
                    <Calendar className="h-12 w-12 mx-auto mb-4" style={{ color: '#5C322B' }} />
                    <h3 className="text-lg font-juv-heading" style={{ color: '#5C322B' }}>Reports</h3>
                    <p className="text-sm mt-2" style={{ color: 'rgba(92, 50, 43, 0.7)' }}>
                      View analytics and performance reports
                    </p>
                  </CardContent>
                </Link>
              </Card>
            </div>

            {/* Additional Navigation Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="bg-white shadow-luxury hover:shadow-xl transition-shadow cursor-pointer" style={{ borderColor: 'rgba(205, 167, 157, 0.3)' }}>
                <Link to="/staff/treatments">
                  <CardContent className="p-6 text-center">
                    <Clock className="h-12 w-12 mx-auto mb-4" style={{ color: '#CDA79D' }} />
                    <h3 className="text-lg font-juv-heading" style={{ color: '#5C322B' }}>Treatment Management</h3>
                    <p className="text-sm mt-2" style={{ color: 'rgba(92, 50, 43, 0.7)' }}>
                      Manage treatments, pricing, and providers
                    </p>
                  </CardContent>
                </Link>
              </Card>

              <Card className="bg-white shadow-luxury hover:shadow-xl transition-shadow cursor-pointer" style={{ borderColor: 'rgba(205, 167, 157, 0.3)' }}>
                <Link to="/staff/settings">
                  <CardContent className="p-6 text-center">
                    <Settings className="h-12 w-12 mx-auto mb-4" style={{ color: '#A55C52' }} />
                    <h3 className="text-lg font-juv-heading" style={{ color: '#5C322B' }}>Settings</h3>
                    <p className="text-sm mt-2" style={{ color: 'rgba(92, 50, 43, 0.7)' }}>
                      Configure system settings and preferences
                    </p>
                  </CardContent>
                </Link>
              </Card>

              <Card className="bg-white shadow-luxury hover:shadow-xl transition-shadow cursor-pointer" style={{ borderColor: 'rgba(205, 167, 157, 0.3)' }}>
                <Link to="/staff/appointments">
                  <CardContent className="p-6 text-center">
                    <Calendar className="h-12 w-12 mx-auto mb-4" style={{ color: '#5C322B' }} />
                    <h3 className="text-lg font-juv-heading" style={{ color: '#5C322B' }}>Appointments</h3>
                    <p className="text-sm mt-2" style={{ color: 'rgba(92, 50, 43, 0.7)' }}>
                      Manage daily schedule and patient check-ins
                    </p>
                  </CardContent>
                </Link>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </RoleProtectedRoute>
  );
};

export default StaffDashboard;
